import React, { useCallback, useState } from "react";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";

interface AssetIdInputProps {
  value: string;
  onChange: (value: string) => void;
  onAssetIdsParsed: (assetIds: string[], invalidIds: string[]) => void;
  placeholder?: string;
  rows?: number;
}

const isValidAssetId = (id: string): boolean => {
  const pattern = /^A\d{15}$/;
  return pattern.test(id.trim());
};

const parseAssetIds = (
  text: string
): { validIds: string[]; invalidIds: string[] } => {
  const ids = text
    .split(/[\n,\s]+/)
    .map((id) => id.trim())
    .filter((id) => id.length > 0);

  const validIds: string[] = [];
  const invalidIds: string[] = [];

  ids.forEach((id) => {
    if (isValidAssetId(id)) {
      validIds.push(id);
    } else {
      invalidIds.push(id);
    }
  });

  return {
    validIds: Array.from(new Set(validIds)), // Remove duplicates
    invalidIds: Array.from(new Set(invalidIds)),
  };
};

const AssetIdInput: React.FC<AssetIdInputProps> = ({
  value,
  onChange,
  onAssetIdsParsed,
  placeholder = "Paste asset IDs here or drag & drop CSV file",
  rows = 4,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);

  const handleCsvFileLoad = useCallback(
    (data: string[][]) => {
      const allIds: string[] = [];

      data.forEach((row) => {
        row.forEach((cell) => {
          if (cell && cell.trim()) {
            allIds.push(cell.trim());
          }
        });
      });

      const { validIds, invalidIds } = parseAssetIds(allIds.join("\n"));
      onAssetIdsParsed(validIds, invalidIds);
    },
    [onAssetIdsParsed]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setIsDragActive(false);
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        Papa.parse(file, {
          complete: (results) => {
            const data = results.data as string[][];
            const nonEmptyRows = data.filter(
              (row) =>
                Array.isArray(row) &&
                row.length > 0 &&
                row.some((cell) => cell && cell.trim() !== "")
            );
            handleCsvFileLoad(nonEmptyRows);
          },
          skipEmptyLines: true,
        });
      }
    },
    [handleCsvFileLoad]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    noClick: true, // Disable click to open file dialog
  });

  const handleParseText = () => {
    const { validIds, invalidIds } = parseAssetIds(value);
    onAssetIdsParsed(validIds, invalidIds);
    onChange(""); // Clear the textarea after parsing
  };

  return (
    <div className="p-fluid">
      <div className="field">
        <label htmlFor="assetIds">Asset IDs Input</label>
        <div
          {...getRootProps()}
          className={`relative ${
            isDragActive
              ? "border-primary bg-primary-reverse"
              : "border-gray-300"
          }`}
        >
          <input {...getInputProps()} />
          <InputTextarea
            id="assetIds"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={rows}
            className={`w-full ${isDragActive ? "border-primary" : ""}`}
          />
          {isDragActive && (
            <div className="absolute inset-0 bg-primary-reverse bg-opacity-20 border-2 border-dashed border-primary rounded-md flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <i className="pi pi-upload text-2xl text-primary mb-2"></i>
                <p className="text-primary font-semibold">
                  Drop CSV file here...
                </p>
              </div>
            </div>
          )}
        </div>
        <small className="text-gray-600">
          Paste asset IDs (one per line or comma separated) or drag & drop a CSV
          file
        </small>
      </div>

      {value.trim() && (
        <Button
          label="Parse Asset IDs"
          icon="pi pi-check"
          onClick={handleParseText}
          className="p-button-outlined"
        />
      )}
    </div>
  );
};

export default AssetIdInput;
